import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocation, useNavigate } from 'react-router'

import { BusinessRoute } from '@/constants/paths'
import { queryKeys } from '@/constants/queryKeys'
import { PaymentRails } from '@/domains/Business/constants'
import {
  KYBApplicationState,
  useFeature,
  useKYBState,
} from '@/domains/Business/hooks'
import { Feature } from '@/domains/Business/types'
import { getCountryNameByISO3 } from '@/lib/country'
import { getFlagUrl, getImage } from '@/lib/images'
import { getAnimationKey } from '@/lib/utils'
import { GoBackButton, StatusBadge, Widget } from '@/shared/components'
import { ChevronRight, DolarappWhite } from '@/shared/icons/outline'
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  MotionDiv,
  Skeleton,
  SlideInScreen,
  Typography,
} from '@/shared/ui'
import { CountryCode } from '@/types/country'

import { getDefaultAccountDetails } from '../../../Accounts/api'
import { AccountType } from '../../../Accounts/types'

type AvailableRails = {
  rails: PaymentRails
  flagCode: string
  countryName: string
  badge: boolean
}

const MX_RAILS: AvailableRails = {
  rails: PaymentRails.MEX,
  flagCode: 'mx',
  countryName: CountryCode.MX,
  badge: false,
}

const EU_RAILS: AvailableRails = {
  rails: PaymentRails.EU,
  flagCode: 'eu',
  countryName: 'EU',
  badge: true,
}

const US_RAILS: AvailableRails = {
  rails: PaymentRails.USA,
  flagCode: 'us',
  countryName: CountryCode.US,
  badge: false,
}

type Props = {
  onContinue: (
    data: { type: 'bank-transfer'; rails: PaymentRails } | { type: 'dolarapp' },
  ) => void
}

export const RecipientAccountTypeStep = ({ onContinue }: Props) => {
  const location = useLocation()
  const intl = useIntl()

  const navigate = useNavigate()

  const kybForEURCState = useKYBState(AccountType.EU_LOCAL)

  const [openKYBDialog, setOpenKYBDialog] = useState(false)

  const { isEnabled: isEURCWalletEnabled, isPending: isEURCWalletPending } =
    useFeature(Feature.EURC_WALLET)

  const accountDetailsQuery = useQuery({
    queryKey: [queryKeys.getDefaultAccountDetails],
    queryFn: getDefaultAccountDetails,
    select: (data) => data.data,
  })

  const availableRails = useMemo(() => {
    const hasUSAccounts = accountDetailsQuery.data?.bankAccounts?.some(
      (account) => [AccountType.ACH, AccountType.WIRE].includes(account.type),
    )

    const rails = [MX_RAILS]

    if (hasUSAccounts) {
      rails.push(US_RAILS)
    }

    if (isEURCWalletEnabled) {
      rails.push(EU_RAILS)
    }

    return rails
  }, [accountDetailsQuery.data?.bankAccounts, isEURCWalletEnabled])

  return (
    <>
      <GoBackButton to={location.state?.from ?? BusinessRoute.Recipients} />

      <SlideInScreen>
        <Typography text="center" variant="h3">
          <FormattedMessage
            defaultMessage="Add new recipient"
            id="recipients.add.title"
          />
        </Typography>

        <div className="p-2" />

        <Typography text="center">
          <FormattedMessage
            defaultMessage="How do you want to add the new recipient?"
            id="recipients.add.subtitle"
          />
        </Typography>

        <div className="p-6" />

        <MotionDiv className="flex flex-col gap-6">
          <Widget
            variant="form"
            title={
              <FormattedMessage
                id="label.bankAccount"
                defaultMessage="Bank account"
              />
            }
          >
            <MotionDiv
              className="flex flex-col gap-3"
              key={getAnimationKey(accountDetailsQuery.isPending)}
            >
              {accountDetailsQuery.isPending || isEURCWalletPending ? (
                <Card
                  size="upload"
                  className="flex w-full items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <Skeleton className="size-7 shrink-0 rounded-full" />

                    <div className="flex flex-col gap-0.5">
                      <Skeleton className="h-5 w-24" />
                      <Skeleton className="h-5 w-24" />
                    </div>
                  </div>
                  <Skeleton className="size-4" />
                </Card>
              ) : (
                availableRails.map((rails) => {
                  return (
                    <Card
                      size="medium"
                      className="flex w-full items-center justify-between"
                      key={rails.rails}
                      asChild
                      scalable
                    >
                      <Button
                        size="inline"
                        variant="ghost"
                        onClick={() => {
                          switch (rails.rails) {
                            case PaymentRails.USA:
                            case PaymentRails.MEX:
                              onContinue({
                                type: 'bank-transfer',
                                rails: rails.rails,
                              })
                              break

                            case PaymentRails.EU: {
                              switch (kybForEURCState) {
                                case KYBApplicationState.COMPLETED:
                                  onContinue({
                                    type: 'bank-transfer',
                                    rails: rails.rails,
                                  })
                                  break
                                case KYBApplicationState.IN_PROGRESS:
                                  setOpenKYBDialog(true)
                                  break
                                case KYBApplicationState.NOT_STARTED:
                                  navigate(BusinessRoute.KYB)
                                  break
                                default:
                                  break
                              }

                              break
                            }

                            default:
                              break
                          }
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <img
                            src={getFlagUrl(rails.flagCode)}
                            className="size-9 shrink-0"
                            alt=""
                            aria-hidden
                          />

                          <div className="flex flex-col">
                            <Typography bold>
                              {(() => {
                                switch (rails.countryName) {
                                  case CountryCode.US:
                                  case CountryCode.MX:
                                    return getCountryNameByISO3(
                                      rails.countryName,
                                      intl.locale,
                                    )

                                  case 'EU':
                                    return (
                                      <FormattedMessage
                                        id="label.europe"
                                        defaultMessage="Europe"
                                      />
                                    )

                                  default:
                                    return null
                                }
                              })()}
                            </Typography>
                            <Typography
                              variant="body-small"
                              className="text-neutral-gray-600"
                            >
                              {(() => {
                                switch (rails.countryName) {
                                  case CountryCode.US:
                                    return (
                                      <FormattedMessage
                                        id="recipients.add.bankAccount.description.US"
                                        defaultMessage="Send USD via Wire or ACH transfer"
                                      />
                                    )
                                  case CountryCode.MX:
                                    return (
                                      <FormattedMessage
                                        id="recipients.add.bankAccount.description.MX"
                                        defaultMessage="Send MXN via SPEI transfer"
                                      />
                                    )

                                  case 'EU':
                                    return (
                                      <FormattedMessage
                                        id="recipients.add.bankAccount.description.EU"
                                        defaultMessage="Send EUR via SEPA transfer"
                                      />
                                    )

                                  default:
                                    return null
                                }
                              })()}
                            </Typography>
                          </div>
                        </div>

                        <div className="flex items-center gap-1">
                          {rails.badge && (
                            <StatusBadge
                              className="uppercase"
                              variant="active"
                              title={intl.formatMessage({
                                id: 'label.new!',
                                defaultMessage: 'New!',
                              })}
                            />
                          )}

                          <ChevronRight className="text-neutral-gray-600 size-6 shrink-0" />
                        </div>
                      </Button>
                    </Card>
                  )
                })
              )}
            </MotionDiv>
          </Widget>

          <Widget
            variant="form"
            title={
              <FormattedMessage
                id="label.dolarAppAccount"
                defaultMessage="DolarApp account"
              />
            }
          >
            <Card
              size="medium"
              className="flex w-full items-center justify-between"
              asChild
              scalable
            >
              <Button
                size="inline"
                variant="ghost"
                onClick={() => onContinue({ type: 'dolarapp' })}
              >
                <div className="flex items-center gap-3">
                  <div className="bg-primary flex size-9 items-center justify-center rounded-full">
                    <DolarappWhite className="size-5 text-white" />
                  </div>

                  <div className="flex flex-col">
                    <div className="flex gap-2">
                      <Typography bold>
                        <FormattedMessage
                          id="label.inDolarApp"
                          defaultMessage="In DolarApp"
                        />
                      </Typography>
                    </div>
                    <Typography
                      variant="body-small"
                      className="text-neutral-gray-600"
                    >
                      <FormattedMessage
                        id="recipients.add.dolarApp.description"
                        defaultMessage="Send USDc instantly and for free via DolarTag"
                      />
                    </Typography>
                  </div>
                </div>
                <ChevronRight className="text-neutral-gray-600 size-6 shrink-0" />
              </Button>
            </Card>
          </Widget>
        </MotionDiv>
      </SlideInScreen>

      <Dialog open={openKYBDialog} onOpenChange={setOpenKYBDialog}>
        <DialogContent hideCloseButtonOnDesktop={false}>
          <img
            className="mx-auto w-32 object-contain"
            src={getImage({ name: 'application-pending' })}
            alt=""
            aria-hidden
          />

          <div className="p-4" />
          <DialogHeader>
            <DialogTitle>
              <FormattedMessage
                id="recipients.add.dialog.kyb.title"
                defaultMessage="We received your EURO application!"
              />
            </DialogTitle>

            <div className="flex flex-col">
              <DialogDescription>
                <FormattedMessage
                  id="recipients.add.dialog.kyb.description"
                  defaultMessage="We will notify you as soon as the review is done or if we need any other info"
                />
              </DialogDescription>
            </div>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </>
  )
}
